import { useCallback, useEffect, useRef, useState } from 'react';
import { coerceToolbarConfig, defaultToolbarConfig, type ToolbarConfig } from '@lapo/core';
import type { ApiClient } from './auth/apiClient.js';

/**
 * Owns the toolbar-note (scratchpad) CONFIG — enable state, summon hotkey, per-dot color/mode. Like
 * `useSettings`, it paints instantly from a localStorage cache, then reconciles with the server
 * (`/me/toolbar-config`) so the config follows the user across devices. Writes are optimistic +
 * debounced: the local state updates immediately and the PUT is coalesced. The dot CONTENT is NOT
 * here — it rides the sync server as `toolbar-*` Yjs docs (see ToolbarDotEditor).
 *
 * `api` is null when signed out / no cloud configured: the hook then stays purely local (cache only),
 * matching the app's offline-first behavior.
 */
const STORAGE_KEY = 'lapo-toolbar-config';
const SAVE_DEBOUNCE_MS = 800;

type ToolbarApi = Pick<ApiClient, 'getToolbarConfig' | 'putToolbarConfig'>;

function loadCache(): ToolbarConfig {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return coerceToolbarConfig(raw ? JSON.parse(raw) : undefined);
  } catch {
    return defaultToolbarConfig();
  }
}

function saveCache(config: ToolbarConfig): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
  } catch {
    /* storage unavailable (private mode); keep running in-memory */
  }
}

export interface UseToolbarConfig {
  config: ToolbarConfig;
  /** Shallow-merge a patch, persist to cache immediately, and debounce a server PUT. */
  update: (patch: Partial<ToolbarConfig>) => void;
}

export function useToolbarConfig(api: ToolbarApi | null): UseToolbarConfig {
  const [config, setConfig] = useState<ToolbarConfig>(loadCache);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reconcile with the server once (and whenever the api identity changes). The cache already
  // painted; the server value wins when it answers. Ignore failures — offline keeps the cache.
  useEffect(() => {
    if (!api) return;
    let cancelled = false;
    void api
      .getToolbarConfig()
      .then((remote) => {
        if (cancelled) return;
        const coerced = coerceToolbarConfig(remote);
        setConfig(coerced);
        saveCache(coerced);
      })
      .catch(() => {
        /* offline / unauthorized → keep the local cache */
      });
    return () => {
      cancelled = true;
    };
  }, [api]);

  // Persist the debounced timer's cleanup so an in-flight save is flushed on unmount patch changes.
  useEffect(
    () => () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    },
    [],
  );

  const update = useCallback(
    (patch: Partial<ToolbarConfig>) => {
      setConfig((prev) => {
        const next = coerceToolbarConfig({ ...prev, ...patch });
        saveCache(next);
        if (api) {
          if (saveTimer.current) clearTimeout(saveTimer.current);
          saveTimer.current = setTimeout(() => {
            void api.putToolbarConfig(next).catch(() => {
              /* offline → the cache holds; next successful load reconciles */
            });
          }, SAVE_DEBOUNCE_MS);
        }
        return next;
      });
    },
    [api],
  );

  return { config, update };
}
